import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

const POLL_INTERVAL_MS = 5000;

function pendingLabel(kind) {
  switch (kind) {
    case 'subordinate': return 'waiting on';
    case 'tool':        return 'awaiting tool';
    default:            return 'pending';
  }
}

function ConvBlock({ conv }) {
  const title = conv.current_activity?.title ?? '(no declared activity)';
  const pending = conv.pending_outbound ?? [];
  return (
    <div className="convBlock">
      <div className="convActivity">{title}</div>
      {pending.length > 0 && (
        <ul className="pendingList">
          {pending.map((p) => (
            <li key={p.corr_id}>
              {pendingLabel(p.kind)} · {p.name}
              {p.subject ? ` · ${p.subject}` : ''}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// AgentActivityRollup renders a single agent's activity rollup.
//
// JOBE-0084: accepts an optional `agent` prop so it can be embedded
// inside another route (e.g. the Agent detail page's Activity tab),
// where the route param key is `:name` rather than `:agent`. The prop
// takes precedence; falling back to `useParams().agent` preserves the
// historical `/agents/:agent/activity` route shape.
export function AgentActivityRollup({ agent: agentProp } = {}) {
  const params = useParams();
  const agent = agentProp ?? params.agent ?? params.name;
  const [rollup, setRollup] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setRollup(null);
    setError(null);
    if (!agent) return undefined;
    const ctrl = new AbortController();
    const run = () => {
      fetch(`/api/agents/${encodeURIComponent(agent)}/activity`, {
        credentials: 'same-origin',
        signal: ctrl.signal,
      })
        .then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json();
        })
        .then((data) => {
          setRollup(data);
          setError(null);
        })
        .catch((err) => {
          if (err.name === 'AbortError') return;
          setError(err.message);
        });
    };
    run();
    const id = setInterval(run, POLL_INTERVAL_MS);
    return () => {
      clearInterval(id);
      ctrl.abort();
    };
  }, [agent]);

  if (error) {
    return (
      <div className="card">
        <h3>{agent} · activity</h3>
        <p className="errorMsg">Failed to load: {error}</p>
      </div>
    );
  }
  if (rollup === null) {
    return (
      <div className="card">
        <h3>{agent} · activity</h3>
        <p>Loading…</p>
      </div>
    );
  }
  const active = rollup.filter(
    (c) => c.current_activity?.title || (c.pending_outbound?.length ?? 0) > 0,
  );
  return (
    <div className="card">
      <h3>{agent} · activity</h3>
      {active.length === 0 ? (
        <p className="idleMsg">idle</p>
      ) : (
        active.map((conv) => (
          <ConvBlock key={conv.conversation_id} conv={conv} />
        ))
      )}
    </div>
  );
}
